import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Button,
  useDisclosure,
  Text,
  HStack,
  Badge,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  VStack,
  Flex,
  RadioGroup,
  Radio,
  Stack,
  Image,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiKey, FiCopy, FiEye, FiEyeOff, FiMonitor } from 'react-icons/fi';
import DataTable, { Column } from '@/components/common/DataTable';
import { useAuthStore } from '@/stores/authStore';
import apiClient, { User, Device } from '@/services/letsparkyClient';
import { useForm } from 'react-hook-form';
import { UserRegisterParams } from '@letsparky/api-v2-client';
import { useTranslation } from 'react-i18next';

// Define local user interface for typing
interface UserData {
  id: string;
  name?: string;
  surname?: string;
  email?: string;
  phone_number?: string;
  role: string;
  tenant_id?: string;
  date_of_birth?: string;
  photo?: string;
  created_at: string;
  [key: string]: any;
}

// Interface for user creation
interface UserFormData {
  email?: string;
  password?: string;
  phone_number?: string;
  name?: string;
  surname?: string;
  date_of_birth?: string;
  photo?: string;
  role?: string;
  tenant_id?: string;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: UserData;
  onSave: (data: UserFormData) => Promise<void>;
  isLoading: boolean;
  isCreating: boolean;
  tenants?: any[];
}

const normalizeDateForInput = (date?: string) => {
  if (!date) return '';
  return date.includes('T') ? date.split('T')[0] : date;
};

const normalizeOptionalValue = (value?: string) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const buildUserUpdatePayload = (
  formData: UserFormData,
  selectedUser: UserData
): Partial<UserFormData> => {
  const candidatePayload: Partial<UserFormData> = {
    name: normalizeOptionalValue(formData.name),
    surname: normalizeOptionalValue(formData.surname),
    email: normalizeOptionalValue(formData.email),
    phone_number: normalizeOptionalValue(formData.phone_number),
    role: normalizeOptionalValue(formData.role),
    photo: normalizeOptionalValue(formData.photo),
    tenant_id: normalizeOptionalValue(formData.tenant_id),
    date_of_birth: normalizeOptionalValue(normalizeDateForInput(formData.date_of_birth)),
  };

  return Object.fromEntries(
    Object.entries(candidatePayload).filter(([key, value]) => {
      if (value === undefined) return false;
      const currentValue =
        key === 'date_of_birth'
          ? normalizeDateForInput(selectedUser.date_of_birth)
          : normalizeOptionalValue(selectedUser[key]);
      return value !== currentValue;
    })
  );
};

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  user,
  onSave,
  isLoading,
  isCreating,
  tenants = [],
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    unregister,
    clearErrors,
    formState: { errors },
  } = useForm<UserFormData>({
    shouldUnregister: true,
    defaultValues: user
      ? {
          name: user.name,
          surname: user.surname,
          email: user.email,
          phone_number: user.phone_number,
          date_of_birth: normalizeDateForInput(user.date_of_birth),
          photo: user.photo,
          role: user.role,
          tenant_id: user.tenant_id,
        }
      : {},
  });

  useEffect(() => {
    if (isOpen) {
      if (!isCreating) {
        unregister('password');
        clearErrors('password');
      }

      reset(
        user
          ? {
              name: user.name,
              surname: user.surname,
              email: user.email,
              phone_number: user.phone_number,
              date_of_birth: normalizeDateForInput(user.date_of_birth),
              photo: user.photo,
              role: user.role,
              tenant_id: user.tenant_id,
            }
          : {}
      );
    }
  }, [isOpen, isCreating, user, reset, unregister, clearErrors]);

  const onSubmit = async (data: UserFormData) => {
    await onSave(data);
  };

  const onInvalidSubmit = () => {
    // Prevent silent failures when a hidden/native validation blocks submission.
    console.error('User form validation failed', errors);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form noValidate onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
          <ModalHeader>{isCreating ? t('users.addUser') : t('users.editUser')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.name}>
                <FormLabel>{t('user.name')}</FormLabel>
                <Input
                  {...register('name', {
                    required: isCreating ? t('users.nameRequired') : false,
                  })}
                />
                {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
              </FormControl>

              <FormControl isInvalid={!!errors.surname}>
                <FormLabel>{t('users.surname')}</FormLabel>
                <Input {...register('surname')} />
              </FormControl>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel>{t('user.email')}</FormLabel>
                <Input
                  type="email"
                  {...register('email', {
                    required: isCreating ? t('users.emailRequired') : false,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('auth.invalidEmail'),
                    },
                  })}
                />
                {errors.email && <FormErrorMessage>{errors.email.message}</FormErrorMessage>}
              </FormControl>

              {isCreating && (
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>{t('common.password')}</FormLabel>
                  <Input
                    type="password"
                    {...register('password', {
                      required: t('users.passwordRequired'),
                      minLength: {
                        value: 6,
                        message: t('users.passwordMinLength'),
                      },
                    })}
                  />
                  {errors.password && <FormErrorMessage>{errors.password.message}</FormErrorMessage>}
                </FormControl>
              )}

              <FormControl isInvalid={!!errors.phone_number}>
                <FormLabel>{t('users.phoneNumber')}</FormLabel>
                <Input {...register('phone_number')} />
              </FormControl>

              <FormControl isInvalid={!!errors.date_of_birth}>
                <FormLabel>{t('users.dateOfBirth')}</FormLabel>
                <Input
                  type="date"
                  {...register('date_of_birth')}
                />
              </FormControl>

              <FormControl isInvalid={!!errors.photo}>
                <FormLabel>{t('users.photo')}</FormLabel>
                <Input {...register('photo')} />
              </FormControl>

              <FormControl isInvalid={!!errors.role}>
                <FormLabel>{t('users.role')}</FormLabel>
                <Select
                  {...register('role', {
                    required: isCreating ? t('users.roleRequired') : false,
                  })}
                >
                  <option value="SYSTEM">{t('users.roleSelectSystem')}</option>
                  <option value="TENANT_ADMIN">{t('users.roleSelectTenantAdmin')}</option>
                  <option value="USER">{t('users.roleSelectUser')}</option>
                </Select>
                {errors.role && <FormErrorMessage>{errors.role.message}</FormErrorMessage>}
              </FormControl>

              <FormControl isInvalid={!!errors.tenant_id}>
                <FormLabel>{t('users.tenant')}</FormLabel>
                <Select
                  {...register('tenant_id')}
                  placeholder={t('users.selectTenant')}
                >
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              type="button"
              isLoading={isLoading}
              onClick={handleSubmit(onSubmit, onInvalidSubmit)}
            >
              {isCreating ? t('common.create') : t('common.update')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

const Users: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { user: currentUser, token } = useAuthStore();
  const isSystem = currentUser?.role === 'SYSTEM';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [users, setUsers] = useState<UserData[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const initialSearch = useMemo(() => searchParams.get('search') || '', []);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedUser, setSelectedUser] = useState<UserData | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [isGeneratingApiKey, setIsGeneratingApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [selectedUserForApiKey, setSelectedUserForApiKey] = useState<UserData | null>(null);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedUserForDevices, setSelectedUserForDevices] = useState<UserData | null>(null);
  const [userDevices, setUserDevices] = useState<Device[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const { 
    isOpen: isApiKeyModalOpen, 
    onOpen: onApiKeyModalOpen, 
    onClose: onApiKeyModalClose 
  } = useDisclosure();
  const { 
    isOpen: isDevicesModalOpen, 
    onOpen: onDevicesModalOpen, 
    onClose: onDevicesModalClose 
  } = useDisclosure();

  const fetchTenants = useCallback(async () => {
    if (!isSystem || !token) return;
    
    try {
      apiClient.client.setToken(token);
      const response = await apiClient.tenants.list({ limit: 100 });
      if (response && response.data && Array.isArray(response.data.data)) {
        setTenants(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  }, [isSystem, token]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      apiClient.client.setToken(token);
      // Build parameters object with search and sorting functionality
      const params: any = {};
      
      // Add search parameter if search query exists
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Add sorting parameters
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_direction = sortDirection;
      }

      const response = await apiClient.users.listPaginated({
        page: currentPage,
        limit: pageSize,
        sort: 'created_at',
        order: 'desc' as const,
      }, params);
      console.log('Fetched users:', response);
      if (response && Array.isArray(response.data)) {
        let filteredData = response.data as UserData[];
        
        // Apply role filtering
        if (roleFilter !== 'ALL') {
          filteredData = filteredData.filter((user: UserData) => user.role === roleFilter);
        }
        
        setUsers(filteredData);
        setTotalItems(roleFilter === 'ALL' ? response.pagination.total || 0 : filteredData.length);
      } else {
        console.error('Invalid response format:', response);
        setUsers([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: t('users.fetchError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setUsers([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, toast, roleFilter, sortBy, sortDirection, token, t]);

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, [fetchUsers, fetchTenants]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (window.confirm(t('users.deleteConfirm'))) {
      try {
        apiClient.client.setToken(token);
        await apiClient.users.delete(id);
        toast({
          title: t('users.deleted'),
          status: 'success',
          duration: 3000,
        });
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      } catch (error) {
        toast({
          title: t('users.deleteError'),
          description: error instanceof Error ? error.message : t('common.unknown'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleEditUser = (user: UserData) => {
    setSelectedUser(user);
    onOpen();
  };

  const handleAddUser = () => {
    setSelectedUser(undefined);
    onOpen();
  };

  const handleSaveUser = async (data: UserFormData) => {
    if (!token) return;
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      if (selectedUser) {
        const updateData = buildUserUpdatePayload(data, selectedUser);
        if (Object.keys(updateData).length === 0) {
          toast({
            title: t('users.noChanges'),
            status: 'info',
            duration: 2500,
            isClosable: true,
          });
          onClose();
          return;
        }

        await apiClient.users.update(selectedUser.id, updateData);
        toast({
          title: t('users.updated'),
          status: 'success',
          duration: 3000,
        });
      } else {
        // Create new user
        await apiClient.auth.register(data as UserRegisterParams);
        toast({
          title: t('users.created'),
          status: 'success',
          duration: 3000,
        });
      }
      
      onClose();
      await fetchUsers();
    } catch (error) {
      toast({
        title: selectedUser ? t('users.saveErrorUpdate') : t('users.saveErrorCreate'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateApiKey = async (user: UserData) => {
    if (!token) return;
    setSelectedUserForApiKey(user);
    setIsGeneratingApiKey(true);
    
    try {
      apiClient.client.setToken(token);
      // Generate a name for the API key based on the user
      const keyName = `${user.name || 'User'}_${new Date().toISOString().split('T')[0]}`;
      
      const response = await apiClient.auth.createApiKey({ 
        name: keyName,
        user_id: user.id // Pass the user ID to generate key for a specific user
      });
      
      if (response && response.data) {
        setNewApiKey(response.data.api_key);
        onApiKeyModalOpen();
        
        toast({
          title: t('users.apiKeyGenerated'),
          description: t('users.apiKeyGeneratedDesc'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: t('users.apiKeyError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGeneratingApiKey(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t('users.copied'),
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleShowUserDevices = async (user: UserData) => {
    if (!token) return;
    setSelectedUserForDevices(user);
    setIsLoadingDevices(true);
    
    try {
      const response = await apiClient.devices.getDevicesForUser(user.id);
      setUserDevices(response.data || []);
      onDevicesModalOpen();
    } catch (error) {
      toast({
        title: t('users.devicesFetchError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const columns: Column<UserData>[] = [
    {
      header: t('user.name'),
      accessor: (user: UserData) => `${user.name || ''} ${user.surname || ''}`.trim() || '-',
      sortable: true,
      sortKey: 'name',
    },
    {
      header: t('user.email'),
      accessor: 'email',
      sortable: true,
      sortKey: 'email',
    },
    {
      header: t('users.columnPhone'),
      accessor: 'phone_number',
      sortable: true,
      sortKey: 'phone_number',
    },
    {
      header: t('users.role'),
      accessor: (user: UserData) => {
        const roleColors = {
          'SYSTEM': 'purple',
          'TENANT_ADMIN': 'blue',
          'USER': 'gray'
        };
        
        const roleName = {
          'SYSTEM': t('users.roleSystemAdmin'),
          'TENANT_ADMIN': t('users.roleTenantAdmin'),
          'USER': t('users.roleRegularUser')
        };
        
        return (
          <Badge colorScheme={roleColors[user.role as keyof typeof roleColors] || 'gray'} 
                 px={2} py={1} borderRadius="md">
            {roleName[user.role as keyof typeof roleName] || user.role}
          </Badge>
        );
      },
      sortable: true,
      sortKey: 'role',
    },
    {
      header: t('users.tenant'),
      accessor: (user: UserData) => {
        if (!user.tenant_id) return <Text color="gray.500">-</Text>;
        
        const tenant = tenants.find((tenantRow) => tenantRow.id === user.tenant_id);
        if (user.role === 'TENANT_ADMIN') {
          return (
            <HStack>
              <Text>{tenant ? tenant.name : user.tenant_id}</Text>
              <Badge colorScheme="green" size="sm" fontSize="xs">{t('users.tenantAdminBadge')}</Badge>
            </HStack>
          );
        }
        return tenant ? tenant.name : user.tenant_id;
      },
      sortable: true,
      sortKey: 'tenant_id',
    },
    {
      header: t('users.columnCreated'),
      accessor: (user: UserData) => new Date(user.created_at).toLocaleDateString(),
      sortable: true,
      sortKey: 'created_at',
    },
  ];

  const actionButtons = (user: UserData) => (
    <>
      <IconButton
        aria-label={t('users.ariaShowDevices')}
        icon={<FiMonitor />}
        size="sm"
        colorScheme="purple"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          handleShowUserDevices(user);
        }}
        mr={1}
      />
      <IconButton
        aria-label={t('users.ariaGenerateApiKey')}
        icon={<FiKey />}
        size="sm"
        colorScheme="teal"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          handleGenerateApiKey(user);
        }}
        mr={1}
      />
      <IconButton
        aria-label={t('users.ariaEditUser')}
        icon={<FiEdit2 />}
        size="sm"
        colorScheme="blue"
        variant="ghost"
        onClick={() => handleEditUser(user)}
      />
      <IconButton
        aria-label={t('users.ariaDeleteUser')}
        icon={<FiTrash2 />}
        size="sm"
        colorScheme="red"
        variant="ghost"
        onClick={() => handleDelete(user.id)}
      />
    </>
  );

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6}>
        <Heading size="lg">{t('users.managementTitle')}</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={handleAddUser}>
          {t('users.addUser')}
        </Button>
      </HStack>

      <Box mb={6} bg="white" p={4} borderRadius="md" shadow="sm">
        <Heading size="sm" mb={3}>{t('users.filterByRole')}</Heading>
        <RadioGroup onChange={setRoleFilter} value={roleFilter}>
          <Stack direction="row" spacing={5}>
            <Radio value="ALL">{t('users.filterAllUsers')}</Radio>
            <Radio value="TENANT_ADMIN">{t('users.filterTenantAdmins')}</Radio>
            <Radio value="SYSTEM">{t('users.filterSystemUsers')}</Radio>
            <Radio value="USER">{t('users.filterRegularUsers')}</Radio>
          </Stack>
        </RadioGroup>
      </Box>

      <DataTable
        columns={columns}
        data={users || []}
        keyField="id"
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        sort_by={sortBy}
        sort_direction={sortDirection}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        onSort={handleSort}
        actionButtons={actionButtons}
      />

      <UserModal
        isOpen={isOpen}
        onClose={onClose}
        user={selectedUser}
        onSave={handleSaveUser}
        isLoading={isSaving}
        isCreating={!selectedUser}
        tenants={tenants}
      />

      {/* API Key Modal */}
      <Modal 
        isOpen={isApiKeyModalOpen} 
        onClose={onApiKeyModalClose}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('users.apiKeyModalTitle', { name: selectedUserForApiKey?.name || t('users.fallbackUserName') })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} fontWeight="bold" color="orange.500">
              {t('users.apiKeyWarning')}
            </Text>
            <Box position="relative" mb={4}>
              <Input
                value={newApiKey || ''}
                isReadOnly
                pr="4rem"
                type={showApiKey ? "text" : "password"}
                fontFamily="monospace"
              />
              <IconButton
                aria-label={showApiKey ? t('users.hideApiKey') : t('users.showApiKey')}
                icon={showApiKey ? <FiEyeOff /> : <FiEye />}
                size="sm"
                position="absolute"
                right="2.5rem"
                top="50%"
                transform="translateY(-50%)"
                onClick={() => setShowApiKey(!showApiKey)}
              />
              <IconButton
                aria-label={t('users.copyApiKey')}
                icon={<FiCopy />}
                size="sm"
                position="absolute"
                right="0.5rem"
                top="50%"
                transform="translateY(-50%)"
                onClick={() => newApiKey && copyToClipboard(newApiKey)}
              />
            </Box>
            <Text fontSize="sm" color="gray.500">
              {t('users.apiKeyDescription')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onApiKeyModalClose}>
              {t('users.done')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* User Devices Modal */}
      <Modal 
        isOpen={isDevicesModalOpen} 
        onClose={onDevicesModalClose}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('users.devicesModalTitle', { name: selectedUserForDevices?.name || t('users.fallbackUserName') })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoadingDevices ? (
              <Flex justify="center" align="center" minH="200px">
                <VStack spacing={4}>
                  <Text>{t('users.loadingDevices')}</Text>
                </VStack>
              </Flex>
            ) : userDevices.length === 0 ? (
              <Flex justify="center" align="center" minH="200px">
                <VStack spacing={4}>
                  <Text fontSize="lg" color="gray.500">{t('users.noDevicesFound')}</Text>
                  <Text fontSize="sm" color="gray.400">
                    {t('users.noDevicesAssigned')}
                  </Text>
                </VStack>
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  {t('users.devicesFoundSummary', { count: userDevices.length })}
                </Text>
                {userDevices.map((device) => (
                  <Box key={device.id} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <HStack justify="space-between" align="start">
                      <VStack align="start" spacing={2}>
                        <HStack>
                          <Text fontWeight="bold">{device.nick_name || device.serial_no}</Text>
                          <Badge 
                            colorScheme={new Date(device.last_connection_date!).getTime() > Date.now() - 10 * 60 * 1000 ? 'green' : 'orange'}
                          >
                            {device.status}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {t('users.serialLabel')}: {device.serial_no}
                        </Text>
                        {device.location && (
                          <Text fontSize="sm" color="gray.600">
                            {t('users.deviceLocationLabel')}: {device.location}
                          </Text>
                        )}
                        {device.last_connection_date && (
                          <Text fontSize="sm" color="gray.600">
                            {t('users.lastSeenLabel')}: {new Date(device.last_connection_date).toLocaleDateString()}
                          </Text>
                        )}
                      </VStack>
                      {device.photo && (
                        <Image 
                          src={device.photo} 
                          alt={device.nick_name || device.serial_no}
                          boxSize="60px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDevicesModalClose}>{t('common.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Users; 